import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CancelRounded as CancelRoundedIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
  ContentCopyRounded as ContentCopyRoundedIcon,
  DataObjectRounded as DataObjectRoundedIcon,
  DescriptionRounded as DescriptionRoundedIcon,
  LockRounded as LockRoundedIcon,
  PublicRounded as PublicRoundedIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

type Capability = {
  title: string;
  description: string;
  supported: boolean;
};

type Resource = {
  label: string;
  path: string;
  auth: "required" | "public";
  get?: boolean;
  post?: boolean;
  put?: boolean;
  delete?: boolean;
  notes?: string;
};

const ApiDocs: React.FC = () => {
  const theme = useTheme();
  const yesColor = theme.palette.success.main;
  const noColor = theme.palette.error.main;
  const [copyNotice, setCopyNotice] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const baseUrl = "/api";

  const capabilities: Capability[] = [
    {
      title: "Read Your Data",
      description:
        "Fetch your income, expenses, debts, goals, accounts, transactions, dashboards, and settings.",
      supported: true,
    },
    {
      title: "Create & Edit Records",
      description:
        "Create/update/delete supported resources (for example income/expenses/debts/goals).",
      supported: true,
    },
    {
      title: "Invite & Share",
      description:
        "Invite group members and share records/pages using secure share links.",
      supported: true,
    },
    {
      title: "Unauthenticated Access",
      description:
        "Access private data without being logged in (share links are the exception).",
      supported: false,
    },
    {
      title: "Bypass Security (RLS)",
      description:
        "Read or mutate records you do not have access to (other users, other groups).",
      supported: false,
    },
    {
      title: "Webhooks",
      description:
        "Receive server-to-server callbacks when data changes (not available yet).",
      supported: false,
    },
  ];

  const resources: Resource[] = [
    {
      label: "Health",
      path: "/health",
      auth: "public",
      get: true,
      notes: "Basic API availability check.",
    },
    {
      label: "Who Am I",
      path: "/whoami",
      auth: "required",
      get: true,
      notes: "Returns current user context.",
    },
    {
      label: "Expenses",
      path: "/expenses",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
    },
    {
      label: "Income",
      path: "/income",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
      notes: "Includes recurrence/series operations.",
    },
    {
      label: "Debts",
      path: "/debts",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
    },
    {
      label: "Goals",
      path: "/goals",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
    },
    {
      label: "Transactions",
      path: "/transactions",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
    },
    {
      label: "Accounts",
      path: "/accounts",
      auth: "required",
      get: true,
      notes: "Also includes organization/custom-name endpoints.",
    },
    {
      label: "Groups & Invitations",
      path: "/groups, /invitations",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
    },
    {
      label: "Shares (Secure Links)",
      path: "/shares, /page-shares, /dashboard-shares, /debt-shares",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
      notes: "Validation endpoints are public for link recipients.",
    },
    {
      label: "Lists",
      path: "/shopping-lists, /shopping-list-items",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
      notes: "Lists and list items.",
    },
    {
      label: "Meal Templates",
      path: "/meal-templates",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
      notes: "Global seeded templates plus user-created/generated templates.",
    },
    {
      label: "Meal Template Generation",
      path: "/meal-templates/generate",
      auth: "required",
      post: true,
      notes:
        "OpenAI-backed meal + ingredient generation. Limited to 5 requests/minute.",
    },
    {
      label: "Ingredients",
      path: "/ingredients",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
      notes: "Ingredient catalog with default price and store metadata.",
    },
    {
      label: "Supplies",
      path: "/supplies",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
      notes:
        "Supplies catalog for ready-to-use items like bread, wraps, and whipped cream.",
    },
    {
      label: "Meal Plans",
      path: "/meal-plans",
      auth: "required",
      get: true,
      post: true,
      put: true,
      delete: true,
      notes: "Date-ranged meal planning containers.",
    },
    {
      label: "Meal Plan Entries",
      path: "/meal-plans/:id/entries, /meal-plan-entries/:id",
      auth: "required",
      post: true,
      put: true,
      delete: true,
      notes:
        "Schedule meals within a plan and update or delete individual entries.",
    },
    {
      label: "User Settings",
      path: "/user-settings",
      auth: "required",
      get: true,
      post: true,
      delete: true,
      notes: "Stores per-user preferences (including Charts layout).",
    },
  ];

  const endpointCatalog = useMemo(() => {
    return resources.flatMap((resource) => {
      const methods = [
        resource.get ? "GET" : null,
        resource.post ? "POST" : null,
        resource.put ? "PUT" : null,
        resource.delete ? "DELETE" : null,
      ].filter(Boolean) as string[];

      return resource.path
        .split(",")
        .map((rawPath) => rawPath.trim())
        .filter(Boolean)
        .map((path) => ({
          resource: resource.label,
          path: `${baseUrl}${path}`,
          methods,
          auth: resource.auth,
          notes: resource.notes || "",
        }));
    });
  }, [resources]);

  const aiDigestPayload = useMemo(
    () => ({
      name: "Focus Finance API",
      baseUrl,
      auth: {
        requiredHeader: "Authorization: Bearer <supabase_access_token>",
        contentType: "application/json",
      },
      capabilities: capabilities.map((capability) => ({
        title: capability.title,
        supported: capability.supported,
        description: capability.description,
      })),
      endpoints: endpointCatalog,
      commonResponses: [401, 403, 429, 500],
    }),
    [baseUrl, capabilities, endpointCatalog]
  );

  const digestJson = useMemo(
    () => JSON.stringify(aiDigestPayload, null, 2),
    [aiDigestPayload]
  );

  const digestMarkdown = useMemo(() => {
    const endpointRows = endpointCatalog
      .map(
        (endpoint) =>
          `| ${endpoint.resource} | \`${endpoint.path}\` | ${endpoint.methods.join(
            ", "
          )} | ${endpoint.auth} | ${endpoint.notes || "-"} |`
      )
      .join("\n");

    return [
      "# Focus Finance API Digest",
      "",
      `- Base URL: \`${baseUrl}\``,
      "- Auth Header: `Authorization: Bearer <supabase_access_token>`",
      "- Content-Type: `application/json`",
      "",
      "## Capabilities",
      ...capabilities.map(
        (capability) =>
          `- ${capability.title}: ${
            capability.supported ? "supported" : "not supported"
          } - ${capability.description}`
      ),
      "",
      "## Endpoints",
      "| Resource | Path | Methods | Auth | Notes |",
      "| --- | --- | --- | --- | --- |",
      endpointRows,
      "",
      "## Common Responses",
      "- 401 Unauthorized",
      "- 403 Forbidden",
      "- 429 Too Many Requests",
      "- 500 Internal Server Error",
    ].join("\n");
  }, [baseUrl, capabilities, endpointCatalog]);

  const handleCopyDigest = async (format: "json" | "md") => {
    const content = format === "json" ? digestJson : digestMarkdown;
    try {
      await navigator.clipboard.writeText(content);
      console.log("[ApiDocs] Copied digest", {
        format,
        characterCount: content.length,
      });
      setCopyNotice({
        open: true,
        message: `Copied API digest as ${format.toUpperCase()}`,
        severity: "success",
      });
    } catch (error) {
      console.error("[ApiDocs] Failed to copy digest", { format, error });
      setCopyNotice({
        open: true,
        message: `Failed to copy ${format.toUpperCase()} digest`,
        severity: "error",
      });
    }
  };

  const YesNoIcon: React.FC<{ value?: boolean }> = ({ value }) => {
    if (value) {
      return <CheckCircleRoundedIcon sx={{ color: yesColor, fontSize: 18 }} />;
    }
    return <CancelRoundedIcon sx={{ color: noColor, fontSize: 18 }} />;
  };

  const tablePaperSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "5px",
    overflow: "hidden",
    bgcolor: "background.paper",
  } as const;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          API
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Use the Focus Finance API to integrate with your own tools, scripts,
          or automations.
        </Typography>
      </Box>

      <Paper sx={tablePaperSx}>
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>Quick Start</Typography>
          <Stack spacing={0.5} sx={{ mt: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              Base URL:{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {baseUrl}
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Auth: Send a Supabase access token in{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                Authorization: Bearer {"<token>"}
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Content-Type:{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                application/json
              </Box>
            </Typography>
          </Stack>
        </Box>
      </Paper>

      <Paper sx={tablePaperSx}>
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>What You Can Do</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              icon={<LockRoundedIcon />}
              label="Auth required"
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12) }}
            />
            <Chip
              size="small"
              icon={<PublicRoundedIcon />}
              label="Public (share links)"
              sx={{ bgcolor: alpha(theme.palette.success.main, 0.12) }}
            />
          </Stack>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: "text.primary" }}>
                Feature
              </TableCell>
              <TableCell
                sx={{ fontWeight: 800, width: 90, color: "text.primary" }}
                align="center"
              >
                Available
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "text.primary" }}>
                Notes
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {capabilities.map((cap) => (
              <TableRow key={cap.title} hover>
                <TableCell sx={{ fontWeight: 700 }}>{cap.title}</TableCell>
                <TableCell align="center">
                  <YesNoIcon value={cap.supported} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {cap.description}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={tablePaperSx}>
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>Resources</Typography>
          <Typography variant="body2" color="text.secondary">
            Endpoints and basic method support.
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: "text.primary" }}>
                Resource
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "text.primary" }}>
                Path
              </TableCell>
              <TableCell
                sx={{ fontWeight: 800, width: 80, color: "text.primary" }}
                align="center"
              >
                GET
              </TableCell>
              <TableCell
                sx={{ fontWeight: 800, width: 80, color: "text.primary" }}
                align="center"
              >
                POST
              </TableCell>
              <TableCell
                sx={{ fontWeight: 800, width: 80, color: "text.primary" }}
                align="center"
              >
                PUT
              </TableCell>
              <TableCell
                sx={{ fontWeight: 800, width: 80, color: "text.primary" }}
                align="center"
              >
                DELETE
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "text.primary" }}>
                Auth
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "text.primary" }}>
                Notes
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map((res) => (
              <TableRow key={res.label} hover>
                <TableCell sx={{ fontWeight: 700 }}>{res.label}</TableCell>
                <TableCell>
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {baseUrl}
                    {res.path}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <YesNoIcon value={res.get} />
                </TableCell>
                <TableCell align="center">
                  <YesNoIcon value={res.post} />
                </TableCell>
                <TableCell align="center">
                  <YesNoIcon value={res.put} />
                </TableCell>
                <TableCell align="center">
                  <YesNoIcon value={res.delete} />
                </TableCell>
                <TableCell>
                  {res.auth === "required" ? (
                    <Chip
                      size="small"
                      icon={<LockRoundedIcon />}
                      label="Required"
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12) }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      icon={<PublicRoundedIcon />}
                      label="Public"
                      sx={{ bgcolor: alpha(theme.palette.success.main, 0.12) }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {res.notes || "—"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={tablePaperSx}>
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Typography sx={{ fontWeight: 800 }}>Common Responses</Typography>
          <Typography variant="body2" color="text.secondary">
            401 Unauthorized, 403 Forbidden, 429 Too Many Requests, 500 Internal
            Server Error.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={tablePaperSx}>
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>AI Digest Export</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleCopyDigest("json")}
              startIcon={<DataObjectRoundedIcon />}
            >
              Copy as JSON
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleCopyDigest("md")}
              startIcon={<DescriptionRoundedIcon />}
            >
              Copy as MD
            </Button>
          </Stack>
        </Box>
        <Stack spacing={1.5} sx={{ p: 1.5 }}>
          <Box>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <ContentCopyRoundedIcon fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                JSON Output
              </Typography>
            </Stack>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: "5px",
                overflowX: "auto",
                fontSize: "0.75rem",
                lineHeight: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              {digestJson}
            </Box>
          </Box>
          <Box>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <ContentCopyRoundedIcon fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Markdown Output
              </Typography>
            </Stack>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: "5px",
                overflowX: "auto",
                fontSize: "0.75rem",
                lineHeight: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              {digestMarkdown}
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={copyNotice.open}
        autoHideDuration={2500}
        onClose={() =>
          setCopyNotice((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={copyNotice.severity}
          onClose={() =>
            setCopyNotice((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{ width: "100%" }}
        >
          {copyNotice.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default ApiDocs;
